/**
 * Filter by class.
 *
 * A row of chips rather than a dropdown, for the same reason the class picker
 * is a grid: 14 options are faster to scan than to open and scroll. It wraps
 * onto two or three lines, which is fine — this sits above a list, not in the
 * middle of a timed flow.
 *
 * `counts`, when supplied, shows how many rows fall in each class so an empty
 * class is obvious before it is clicked.
 */
import { classesByAgeGroup, classLabel } from '@shared/catalog';

export default function ClassFilter({
  value,
  onChange,
  counts,
  className = '',
}: {
  value: string;
  onChange: (classKey: string) => void;
  counts?: Record<string, number>;
  className?: string;
}) {
  const groups = classesByAgeGroup();

  return (
    <div className={className}>
      <div className="mb-2 text-[10px] font-bold uppercase tracking-button text-white/30">
        Class
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Chip active={value === 'all'} onClick={() => onChange('all')} label="All classes" />

        {groups.map((group) => (
          <span key={group.ageGroup} className="contents">
            <span className="mx-1 select-none text-[10px] uppercase tracking-button text-white/20">
              {group.ageGroup}
            </span>
            {group.classes.map((c) => (
              <Chip
                key={c.key}
                active={value === c.key}
                onClick={() => onChange(c.key)}
                label={c.display}
                count={counts?.[c.key]}
                title={classLabel(c.key)}
              />
            ))}
          </span>
        ))}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
  count,
  title,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className={`rounded-btn border px-3 py-1.5 text-xs font-semibold transition-all duration-brand ${
        active
          ? 'border-brand bg-brand text-paper'
          : 'border-edge bg-surface text-white/55 hover:border-brand hover:text-paper'
      }`}
    >
      {label}
      {count !== undefined && <span className="tabular ml-1.5 opacity-60">{count}</span>}
    </button>
  );
}
